import { AlertCircle } from "lucide-react";

import { Dialog } from "components/ui/Dialog";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { formatDate } from "utils/dateFormatter";

import styles from "./DeaspDetailsModal.module.css";

const CERTIFICATE_TYPE_LABELS = {
  MED1: "Medical Certificate 1",
  MED2: "Medical Certificate 2",
};

const PHYSICAL_EFFORT_LABELS = {
  LIGHT: "Light",
  MODERATE: "Moderate",
  HEAVY: "Heavy",
  NOT_APPLICABLE: "Not Applicable",
};

const PHYSICAL_EFFORT_COLORS = {
  LIGHT: PILL_COLORS.reactionNo,
  MODERATE: PILL_COLORS.statusWarning,
  HEAVY: PILL_COLORS.reactionYes,
  NOT_APPLICABLE: PILL_COLORS.reactionUnknown,
};

const YES_NO_PILL = {
  Yes: PILL_COLORS.sentYes,
  No: PILL_COLORS.sentNo,
};

const DetailRow = ({ label, children }) => (
  <div className={styles.row}>
    <span className={styles.label}>{label}</span>
    <span className={styles.value}>{children ?? "—"}</span>
  </div>
);

export const DeaspDetailsModal = ({ certificate, onClose }) => {
  const cert = certificate?.deaspCertificate;
  const errorMessage = certificate?.error_message;
  const messageControlId = certificate?.messageControlId ?? cert?.messageControlId;

  const physicalEffortLabel = PHYSICAL_EFFORT_LABELS[cert?.physicalEffortRequired];
  const physicalEffortColors =
    PHYSICAL_EFFORT_COLORS[cert?.physicalEffortRequired] ?? PILL_COLORS.reactionUnknown;

  const inHospitalColors = YES_NO_PILL[cert?.inHospital];
  const finalCertColors = YES_NO_PILL[cert?.finalCertIndicator];

  return (
    <Dialog
      open={!!certificate}
      onClose={onClose}
      title="DEASP Certificate Details"
      width={560}
    >
      <div className={styles.body}>
        {!!errorMessage && (
          <div className={styles.errorBox}>
            <AlertCircle size={16} className={styles.errorIcon} />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className={styles.section}>
          <p className={styles.sectionTitle}>Certificate Information</p>
          <DetailRow label="Doctor">
            {cert?.dspPanelDoctor
              ? `${cert.dspPanelDoctor.firstName} ${cert.dspPanelDoctor.surname}`
              : null}
          </DetailRow>
          <DetailRow label="Certificate Type">
            {CERTIFICATE_TYPE_LABELS[cert?.certFormType] ?? cert?.certFormType ?? "—"}
          </DetailRow>
          <DetailRow label="Condition Code">
            {cert?.natureOfConditionCode}
          </DetailRow>
          <DetailRow label="Nature of Condition">
            {cert?.natureOfCondition}
          </DetailRow>
          <DetailRow label="Sick From">
            {formatDate(cert?.sickFromDate)}
          </DetailRow>
          <DetailRow label="Sick To">
            {formatDate(cert?.sickToDate)}
          </DetailRow>
          <DetailRow label="Is Final">
            {cert?.finalCertIndicator ? (
              finalCertColors ? (
                <Pill {...finalCertColors}>{cert.finalCertIndicator}</Pill>
              ) : (
                cert.finalCertIndicator
              )
            ) : null}
          </DetailRow>
          <DetailRow label="Message Control ID">
            {messageControlId}
          </DetailRow>
        </div>

        <div className={styles.section}>
          <p className={styles.sectionTitle}>Clinical Details</p>
          <DetailRow label="Physical Effort Required">
            {physicalEffortLabel ? (
              <Pill {...physicalEffortColors}>{physicalEffortLabel}</Pill>
            ) : null}
          </DetailRow>
          <DetailRow label="Hospitalised">
            {cert?.inHospital ? (
              inHospitalColors ? (
                <Pill {...inHospitalColors}>{cert.inHospital}</Pill>
              ) : (
                cert.inHospital
              )
            ) : null}
          </DetailRow>
        </div>
      </div>
    </Dialog>
  );
};
